import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { StaggerContainer, StaggerItem } from '@/components/ui/motion';

interface ProcessStep {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface AdoptionProcessOverviewProps {
  steps: ProcessStep[];
}

const AdoptionProcessOverview = ({ steps }: AdoptionProcessOverviewProps) => {
  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-secondary/5">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl lg:text-5xl font-headline font-semibold text-foreground">
            Your Adoption Journey
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We make the adoption process simple, transparent, and supportive every step of the way.
          </p>
        </div>

        <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <StaggerItem key={step.id} className="relative">
              <div className="bg-card rounded-2xl p-6 shadow-soft hover:shadow-elevated transition-all duration-300 h-full">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                      <Icon name={step.icon as any} size={28} variant="outline" className="text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white font-headline font-semibold text-sm">
                      {index + 1}
                    </div>
                  </div>

                  <h3 className="text-xl font-headline font-semibold text-foreground">
                    {step.title}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <Icon name="ChevronRightIcon" size={24} variant="outline" className="text-primary" />
                </div>
              )}
            </StaggerItem>
          ))}
        </StaggerContainer>

        <div className="text-center mt-12">
          <Link
            href="/adoption-process"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-white font-headline font-semibold rounded-lg hover:bg-primary/90 hover:shadow-elevated transition-all duration-300"
          >
            <Icon name="ClipboardDocumentCheckIcon" size={20} variant="outline" />
            <span>Learn About Adoption Process</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AdoptionProcessOverview;